import { defaults, kebabCase, truncate } from 'lodash';
import { action, computed, observable } from 'mobx';
import moment, { Moment } from 'moment';
import { youtubeLinkParser } from '../utils/youtube';
import { FUTURE_SPEECHES_KEYWORD, SpeechesData } from './SpeechesData';

export interface SpeakerData {
    name: string;
    photo: string;
    title: string,
    twitter?: string;
    github?: string;
}

type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;

export interface SpeakerObject extends Omit<SpeakerData, 'photo'> {
    photo: Promise<string>;
}

type MediaType = 'conference' | 'abstract' | 'video' | 'blog' | 'article' | 'slides';

export interface MediaData {
    type: MediaType;
    caption: string;
    link: string;
}

interface Data {
    Media: MediaData[];
    Date: string;
    Text: string;
    Conference: string;
    Title: string;
    Speakers: SpeakerData[];
    Country: string;
    Location: string
}

export class SpeechData {
    @observable Conference: string;
    @observable Country: string;
    @observable Date: string;
    @observable Location: string;
    @observable Media: MediaData[];
    @observable Speakers: SpeakerData[];
    @observable Text: string;
    @observable Title: string;
    @observable isVisible = false;
    @action setVisibility = (value: boolean) => this.isVisible = value;

    constructor(data: Data, private speeches: SpeechesData) {
        defaults(this, data, {
            Media: [],
            Speakers: [],
        });
    }

    @computed get conferenceLink(): string {
        return this.Media.filter(m => m.type === 'conference').map(m => m.link).pop();
    };

    @computed get speakers(): SpeakerObject[] {
        return this.Speakers.map(speaker => ({ ...speaker, photo: this.speeches.speakersPhotos[speaker.name] }));
    }

    @computed get flag(): Promise<string> {
        return this.speeches.countriesFlags[this.Country];
    }

    @computed get link(): string {
        const { Title, Conference, Location } = this;
        const what = truncate(kebabCase(Title.toLowerCase()), { length: 40, omission: '...', separator: /-/ });
        const where = kebabCase(Conference.toLowerCase() + ' ' + Location);
        return what + '(at)' + where;
    }

    @computed get moment(): Moment {
        return moment(this.Date);
    }

    @computed get year(): string {
        return this.moment.isAfter(new Date()) ? FUTURE_SPEECHES_KEYWORD : this.moment.year().toString();
    }

    @computed get videos(): MediaData[] {
        const mediaData = this.Media;
        return [...mediaData].filter(m => m.type === 'video');
    }

    @computed get youtubeIds(): string[] {
        return this.videos.map(m => youtubeLinkParser(m.link)).filter(Boolean);
    }
}
